import { useQueryClient } from '@tanstack/react-query';
import { omit } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Fragment } from 'react/jsx-runtime';
import { filter, tap } from 'rxjs';

import { QUERY_KEY_CONST, TOOL_CONST } from '@/constant';
import { useAllObjectClassListQuery, useSubscriptions } from '@/hook';
import {
  RightPanelClose,
  RightPanelOpen,
  VFlex,
  VIconButton,
  VStack,
} from '@/package';
import { useWebSocket } from '@/provider';
import {
  useReviewStore,
  useTaskFrameStore,
  useToolMenuStore,
  useToolSettingStore,
} from '@/store';
import type { FrameData } from '@/type';

import ToolPcdCanvasDefaultViewMode from '../ToolCanvas/ToolPcdCanvasDefaultViewMode';
import ToolPcdCanvasFSTViewMode from '../ToolCanvas/ToolPcdCanvasFSTViewMode';
import { usePcdManager } from '../ToolCanvas/hook';
import * as EditLinkedTrackID from './EditLinkedTrackID';
import LabelOperation from './LabelOperation';
import ShortcutSettingModal from './ShortcutSettingModal';
import { ToolImagePopup } from './ToolImagePopup';
import ToolInitializeLoading from './ToolInitializeLoading';
import { ToolLabelingBoxValidationPopup } from './ToolLabelingBoxValidationPopup';
import { useToolPlayControllerContext } from './ToolPlayController';
import { TempToolReviewCommentFormViewer } from './ToolReviewCommentFormViewer';
import { ToolSearchTrackIDPopup } from './ToolSearchTrackIDPopup';
import ToolSettingPopup from './ToolSettingPopup';
import { useToolShortcutRegister } from './hook';

const PANEL_WIDTH_PIXEL = 384;
const LABELING_PANNEL_REM = 2;

const ToolMain = () => {
  const {
    cameraPannelShow,
    toolSettingVisible,
    toggleCameraPannel,
    imagePopupVisible,
    shortcutPopupVisible,
    setShortcutPopupVisible,
    initLabelingBoxOpactity,
  } = useToolSettingStore();
  const [isFrameFileLoading, currentFrame] = useTaskFrameStore(state => [
    state.isFrameFileLoading,
    state.currentFrame,
  ]);
  const reviewCommentVisible = useReviewStore(
    state => state.reviewCommentVisible,
  );
  const findByName = useToolMenuStore(state => state.findByName);
  const { $toolPlayController, isPlaybarOpen } = useToolPlayControllerContext();
  const { alarmMessage$ } = useWebSocket();
  const queryClient = useQueryClient();
  const shortcutPopupTool = findByName('shortcutPopup');
  const { data: objectClassList } = useAllObjectClassListQuery();
  const [isInitialized, setIsInitialized] = useState(false);
  const currentFrameSeqRef = useRef<FrameData['frameSeq']>(-1);

  const rootFontSize = parseFloat(
    getComputedStyle(document.documentElement).fontSize,
  );
  const labelingPannelWidth =
    window.innerWidth - LABELING_PANNEL_REM * rootFontSize;
  const panelSizePercentage = (PANEL_WIDTH_PIXEL / labelingPannelWidth) * 100;

  usePcdManager();
  useToolShortcutRegister();

  useSubscriptions(
    () => [
      alarmMessage$
        .pipe(
          filter(payload => payload.alarmType === 'SyncFrameImageDone'),
          tap(() => {
            queryClient.invalidateQueries({
              queryKey: QUERY_KEY_CONST.FRAME_KEYS.getDetail(),
            });
          }),
        )
        .subscribe(),
    ],
    [alarmMessage$, queryClient],
  );

  useEffect(() => {
    setShortcutPopupVisible(!!shortcutPopupTool?.isSelected);
  }, [shortcutPopupTool?.isSelected]);

  useEffect(() => {
    currentFrameSeqRef.current = currentFrame?.frameSeq ?? -1;
  }, [currentFrame?.frameSeq]);

  useEffect(() => {
    if (!objectClassList || isInitialized || !currentFrame?.frameSeq) return;

    initLabelingBoxOpactity(objectClassList.map(item => item.id.toString()));
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsInitialized(true);
  }, [objectClassList, isInitialized, currentFrame?.frameSeq]);

  return (
    <VStack className="relative h-full w-full overflow-hidden">
      <ToolInitializeLoading />

      <div className="relative flex h-full overflow-hidden">
        <div className="relative size-full">
          <PanelGroup
            direction="horizontal"
            autoSaveId={'toolContainer__top__pcdViewer'}
          >
            <Panel order={1}>
              <div className={'flex h-full flex-col gap-1'}>
                <div className={'relative flex-1'}>
                  {isFrameFileLoading && (
                    <VFlex
                      className="absolute top-0 right-0 bottom-[74px] left-0 z-10 bg-black/50"
                      items="center"
                      justify="center"
                    />
                  )}
                  <ToolPcdCanvasDefaultViewMode
                    initCameraOptions={{
                      up: [0, 0, 1],
                      position: [0, 0, 50],
                      near: 0.01,
                      orthographic: {
                        zoom: 50,
                      },
                      perpective: {
                        zoom: 1.5,
                      },
                    }}
                    enabledFullScreen
                    enabledDragBox
                  />

                  <VIconButton
                    Icon={cameraPannelShow ? RightPanelClose : RightPanelOpen}
                    className="absolute top-0 right-[-4px] size-6! rounded-r-none! bg-white! text-primary-500!"
                    onClick={toggleCameraPannel}
                  />
                </div>
              </div>
            </Panel>

            <PanelResizeHandle className="w-1" />

            <Panel
              defaultSize={panelSizePercentage}
              minSize={5.5}
              order={2}
              hidden={!cameraPannelShow}
            >
              <div className={'h-full w-full'}>
                <div
                  className={'flex flex-col gap-1'}
                  style={{
                    height: isPlaybarOpen
                      ? `calc(100% - ${$toolPlayController.current?.offsetHeight ?? 0}px)`
                      : '100%',
                  }}
                >
                  <PanelGroup
                    direction="vertical"
                    autoSaveId={'toolContainer__right__divided'}
                  >
                    {Object.entries(
                      omit(TOOL_CONST.TOOL_CAMERA_VIEW_MODE, ['DEFAULT']),
                    ).map(([key, viewMode], index, arr) => {
                      const initCameraOptions =
                        TOOL_CONST.DEFAULT_CAMERA_OPTIONS_BY_VIEW_MODE[
                          key as keyof typeof TOOL_CONST.TOOL_CAMERA_VIEW_MODE
                        ];

                      return (
                        <Fragment key={viewMode}>
                          <Panel minSize={10}>
                            <div className={'lex h-full flex-1 flex-col'}>
                              <ToolPcdCanvasFSTViewMode
                                initCameraOptions={{
                                  ...initCameraOptions,
                                  far: 5000,
                                  near: 0.01,
                                }}
                                viewMode={viewMode}
                              />
                            </div>
                          </Panel>
                          {arr.length - 1 !== index && (
                            <PanelResizeHandle className="h-1" />
                          )}
                        </Fragment>
                      );
                    })}
                  </PanelGroup>
                </div>
              </div>
            </Panel>
          </PanelGroup>

          {reviewCommentVisible && <TempToolReviewCommentFormViewer />}
          {toolSettingVisible && <ToolSettingPopup />}
          {imagePopupVisible && <ToolImagePopup />}
          {shortcutPopupVisible && <ShortcutSettingModal />}

          <ToolLabelingBoxValidationPopup />
          <ToolSearchTrackIDPopup />
          <EditLinkedTrackID.Modal />
          {isInitialized && <EditLinkedTrackID.Popup />}
        </div>
      </div>

      <LabelOperation />
    </VStack>
  );
};

export default ToolMain;
